import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import CashRunwayChart from "../../components/director/CashRunwayChart";
import ExcludedTitlesNotice from "../../components/director/ExcludedTitlesNotice";
import ProjectCapitalChart from "../../components/director/ProjectCapitalChart";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import ScopeNotice from "../../components/director/ScopeNotice";
import { useDirectorContext } from "@contexts/director";
import {
  balanceBlockerMessage,
  compactCents,
  formatCivilDate,
  formatCompetenceShort,
  formatFreshness,
} from "../../utils/directorFormat";
import {
  companyQuery,
  currentCompetence,
  oldestExtractedAt,
  scenarioLabel,
  shiftCompetence,
} from "./module1Utils";

const SCENARIOS = ["base", "conservative", "stress"];

const joinNames = (names) => {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} e ${names.at(-1)}`;
};

const earliest = (values) => values.filter(Boolean).sort()[0] || null;

const civilDate = (value) => (
  typeof value === "string" ? formatCivilDate(value.slice(0, 10)) : "data não informada"
);

const BlockedBalance = ({ blockers = [], canReviewGovernance, selectedCompanyId }) => <section
  className="director-state director-capital-blocked"
  role="status"
  aria-label="Saldo projetado bloqueado"
>
  <strong>O aporte no caixa ainda não pode ser calculado</strong>
  <p>Ele depende do saldo inicial de cada caixa e das fontes fechadas. Hoje falta:</p>
  <ul>
    {(blockers.length ? blockers : ["unknown_blocker"]).map((code) => <li key={code}>{balanceBlockerMessage(code)}</li>)}
  </ul>
  {canReviewGovernance && <Link className="director-button" to={`/dashboard/director/governance${companyQuery(selectedCompanyId)}`}>
    Revisar saldos e colchão
  </Link>}
</section>;

const peakText = (peak) => (
  peak?.capitalCents > 0 && peak.month
    ? `${compactCents(peak.capitalCents)} em ${formatCompetenceShort(peak.month)}`
    : "Não fica negativo"
);

const peakValue = (peak) => (peak?.capitalCents > 0 ? compactCents(peak.capitalCents) : "R$ 0");

// Resposta 1: quanto capital as obras consomem desde o início, sem depender de saldo.
export const ProjectCapitalSection = ({ surface, companyId }) => {
  const { data } = surface;
  if (!data) return <ModuleState {...surface} onRetry={surface.reload} />;
  if (data.status !== "ready") return null;
  const { portfolio, projects = [], outsideProjects, loads = {} } = data;
  const flags = data.qualityFlags || [];
  const several = projects.length > 1;
  return <section className="director-capital-consumption" aria-labelledby="director-capital-consumption-title">
    <header className="director-module1__section-header">
      <span className="director-eyebrow">Capital que a obra consome</span>
      <h2 id="director-capital-consumption-title">Quanto as obras tiram do seu caixa até se pagarem</h2>
      <p>
        Fluxo acumulado desde {formatCompetenceShort(portfolio.firstMonth)}: entradas menos saídas de cada obra.
        Não depende do saldo bancário.
      </p>
    </header>
    <section className="director-module1__metrics director-module1__metrics--four" aria-label="Capital que as obras consomem">
      <MetricCard
        label={several ? "Pico do portfólio" : "Pico de consumo"}
        value={peakValue(portfolio.peak)}
        note={portfolio.peak.month
          ? `Em ${formatCompetenceShort(portfolio.peak.month)}, com a projeção`
          : "O acumulado não fica negativo"}
        tone={portfolio.peak.capitalCents > 0 ? "orange" : "green"}
      />
      <MetricCard
        label="Pico até hoje"
        value={peakValue(portfolio.realizedPeak)}
        note={portfolio.realizedPeak.month ? `Em ${formatCompetenceShort(portfolio.realizedPeak.month)}` : "Só com o realizado"}
      />
      <MetricCard
        label="Acumulado até hoje"
        value={compactCents(portfolio.toDateCumulativeCents)}
        note={`Recebido ${compactCents(portfolio.receivedCents)} · pago ${compactCents(portfolio.paidCents)}`}
        tone={portfolio.toDateCumulativeCents < 0 ? "pressure" : "green"}
      />
      <MetricCard
        label="Última carga completa"
        value={loads.lastFullLoadAt ? formatFreshness(loads.lastFullLoadAt) : "Ainda não feita"}
        note={loads.lastFullLoadAt ? "Histórico desde o início da obra" : "Meses antes da janela diária podem faltar"}
      />
    </section>
    <section className="director-runway" aria-label="Curva do capital consumido">
      <header>
        <div>
          <span className="director-eyebrow">Acumulado mês a mês</span>
          <h2>{several ? "Curva por obra e do portfólio" : "Curva da obra"}</h2>
        </div>
        <p>
          Linha cheia: realizado. Tracejada: projeção com o que falta receber, o contratado e o planejado.
          Abaixo de zero é capital seu dentro da obra.
        </p>
      </header>
      <ProjectCapitalChart portfolio={portfolio} projects={projects} />
    </section>
    <ExcludedTitlesNotice summary={data.excludedTitles} companyId={companyId} />
    {flags.includes("erp_open_forecast_not_committed") && <p className="director-module1__callout">
      A projeção inclui títulos de previsão do ERP (PRV), contados como planejado e não como compromisso.
    </p>}
    {flags.includes("peak_at_horizon_end") && <p className="director-module1__callout" role="note">
      O pico cai no último mês com movimento: a curva ainda desce ali. Confira os compromissos de longo prazo.
    </p>}
    <div className="director-table-scroll">
      <table className="director-table director-owner-table" aria-label="Capital por obra">
        <thead>
          <tr>
            <th scope="col">Obra</th>
            <th scope="col">Desde</th>
            <th scope="col">Pico com projeção</th>
            <th scope="col">Pico até hoje</th>
            <th scope="col">Acumulado hoje</th>
            <th scope="col">Recebido</th>
            <th scope="col">Pago</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => <tr
            key={project.projectKey}
            data-uncovered={project.toDateCumulativeCents < 0 ? "true" : "false"}
          >
            <th scope="row">{project.name}</th>
            <td>{formatCompetenceShort(project.firstMonth)}</td>
            <td>{peakText(project.peak)}</td>
            <td>{peakText(project.realizedPeak)}</td>
            <td>{compactCents(project.toDateCumulativeCents)}</td>
            <td>{compactCents(project.receivedCents)}</td>
            <td>{compactCents(project.paidCents)}</td>
          </tr>)}
        </tbody>
      </table>
    </div>
    {outsideProjects && <p className="director-module1__callout">
      {outsideProjects.label} ficam fora do capital por obra: recebido {compactCents(outsideProjects.receivedCents)},
      pago {compactCents(outsideProjects.paidCents)} no mesmo período.
    </p>}
  </section>;
};

const Capital = () => {
  const { selectedCompanyId, accessByCompany = {} } = useDirectorContext();
  const [scenario, setScenario] = useState("base");
  const query = useMemo(() => {
    const from = currentCompetence();
    return { from, to: shiftCompetence(from, 11), scenario };
  }, [scenario]);
  const surface = useDirectorSurface("balance-projection", query);
  const capitalQuery = useMemo(() => ({ scenario }), [scenario]);
  const capitalSurface = useDirectorSurface("project-capital", capitalQuery);
  const { data } = surface;
  const capabilities = accessByCompany[selectedCompanyId]?.capabilities || [];
  const poolingGroups = data?.poolingGroups || [];
  const labelFor = (poolingGroupId) => (
    poolingGroups.find((pool) => pool.poolingGroupId === poolingGroupId)?.label || poolingGroupId
  );
  const poolLabels = Object.fromEntries(poolingGroups.map((pool) => [pool.poolingGroupId, pool.label || pool.poolingGroupId]));
  const risk = data?.risk || {};
  const byPool = risk.byPoolingGroup || [];
  const riskFor = (poolingGroupId) => byPool.find((item) => item.poolingGroupId === poolingGroupId) || {};
  const openingFor = (poolingGroupId) => (
    (data?.openingBalance?.pools || []).find((item) => item.poolingGroupId === poolingGroupId) || {}
  );
  const minimumPool = [...byPool]
    .filter((item) => Number.isSafeInteger(item.minimumBalanceCents))
    .sort((left, right) => left.minimumBalanceCents - right.minimumBalanceCents)[0];
  const minimumMonth = minimumPool
    ? poolingGroups.find((pool) => pool.poolingGroupId === minimumPool.poolingGroupId)?.minimumBalanceMonth
    : null;
  const firstNegative = earliest(byPool.map((item) => item.firstNegativeMonth)) || risk.firstNegativeMonth || null;
  const firstBelowBuffer = earliest(byPool.map((item) => item.firstBelowBufferMonth)) || risk.firstBelowBufferMonth || null;
  const criticalMonth = firstNegative || firstBelowBuffer;
  const criticalPool = byPool.find((item) => (
    firstNegative ? item.firstNegativeMonth === firstNegative : item.firstBelowBufferMonth === firstBelowBuffer
  ));
  const needing = byPool.filter((item) => item.fundingGapCents > 0).map((item) => labelFor(item.poolingGroupId));
  const surplus = poolingGroups
    .filter((pool) => !(riskFor(pool.poolingGroupId).fundingGapCents > 0))
    .map((pool) => pool.label || pool.poolingGroupId);
  const openingAsOf = earliest((data?.openingBalance?.pools || []).map((item) => item.asOf));
  const partialPools = poolingGroups.filter((pool) => pool.complete === false).map((pool) => pool.poolingGroupId);
  const freshness = oldestExtractedAt(data?.freshness);
  const available = data?.balanceAvailable === true;

  return <ModuleShell
    title="Capital necessário"
    description={available && openingAsOf
      ? `Quanto capital as obras consomem e quanto cada caixa vai precisar. Saldos iniciais informados em ${civilDate(openingAsOf)}.`
      : "Quanto capital as obras consomem e, com o saldo inicial, quanto cada caixa vai precisar."}
    badge={scenarioLabel(scenario)}
    qualitySources={[capitalSurface, surface]}
    showProjectFilter={false}
  >
    <section className="director-module1__filters">
      <label className="director-filter-field">
        <span>Cenário</span>
        <select value={scenario} onChange={(event) => setScenario(event.target.value)}>
          {SCENARIOS.map((value) => <option key={value} value={value}>{scenarioLabel(value)}</option>)}
        </select>
      </label>
    </section>
    <ProjectCapitalSection surface={capitalSurface} companyId={selectedCompanyId} />
    <header className="director-module1__section-header">
      <span className="director-eyebrow">Aporte necessário no caixa</span>
      <h2>Quanto cada caixa precisa nos próximos 12 meses</h2>
    </header>
    <ModuleState
      {...surface}
      availability={data?.status === "no_data" || data?.status === "empty" ? "empty" : undefined}
      onRetry={surface.reload}
    />
    {data && !available && data.status !== "no_data" && <BlockedBalance
      blockers={data.blockers || []}
      canReviewGovernance={capabilities.includes("director.governance.read")}
      selectedCompanyId={selectedCompanyId}
    />}
    {data && available && <>
      <ScopeNotice
        completePortfolioScope={partialPools.length ? false : data.scope?.completePortfolioScope}
        partialPoolingGroupIds={partialPools}
      />
      <section className="director-module1__metrics director-module1__metrics--four" aria-label="Indicadores de capital">
        <MetricCard
          label="Menor saldo projetado"
          value={compactCents(risk.minimumBalanceCents)}
          note={minimumPool
            ? `${labelFor(minimumPool.poolingGroupId)}${minimumMonth ? `, em ${formatCompetenceShort(minimumMonth)}` : ""}`
            : "Sem caixa com saldo projetado"}
          tone={risk.minimumBalanceCents < 0 ? "pressure" : "neutral"}
        />
        <MetricCard
          label="Primeiro mês crítico"
          value={criticalMonth ? formatCompetenceShort(criticalMonth) : "Nenhum"}
          note={criticalMonth
            ? `${criticalPool ? `${labelFor(criticalPool.poolingGroupId)} fica` : "Um caixa fica"} ${firstNegative ? "abaixo de zero" : "abaixo do colchão"}`
            : "Nenhum caixa fica abaixo do colchão"}
          tone={criticalMonth ? "orange" : "green"}
        />
        <MetricCard
          label="Aporte para manter o colchão"
          value={compactCents(risk.fundingGapCents)}
          note={needing.length ? needing.join(" + ") : "Nenhum caixa precisa de aporte"}
          tone={risk.fundingGapCents > 0 ? "pressure" : "green"}
        />
        <MetricCard
          label="Aporte para não ficar negativo"
          value={compactCents(risk.gapToZeroCents)}
          note="Sem colchão"
        />
      </section>
      <CashRunwayChart
        months={data.months || []}
        showTable={false}
        poolLabels={poolLabels}
        eyebrow="Caixa a caixa"
        title="Saldo projetado por caixa"
        description="Próximos 12 meses. A linha tracejada de cada caixa é o colchão; a preta é o zero."
      />
      <section className="director-module1__split">
        <article className="director-module1__panel">
          <header>
            <div>
              <span className="director-eyebrow">Caixa por caixa</span>
              <h2>Quanto cada caixa precisa</h2>
            </div>
          </header>
          <div className="director-table-scroll">
            <table className="director-table director-owner-table" aria-label="Caixa por caixa">
              <thead>
                <tr>
                  <th scope="col">Caixa</th>
                  <th scope="col">Saldo hoje</th>
                  <th scope="col">Menor saldo</th>
                  <th scope="col">Quando</th>
                  <th scope="col">Fica negativo em</th>
                  <th scope="col">Aporte necessário</th>
                </tr>
              </thead>
              <tbody>
                {poolingGroups.map((pool) => {
                  const poolRisk = riskFor(pool.poolingGroupId);
                  return <tr key={pool.poolingGroupId} data-uncovered={poolRisk.fundingGapCents > 0 ? "true" : "false"}>
                    <th scope="row">{pool.label || pool.poolingGroupId}</th>
                    <td>{compactCents(openingFor(pool.poolingGroupId).balanceCents)}</td>
                    <td>{compactCents(poolRisk.minimumBalanceCents)}</td>
                    <td>{pool.minimumBalanceMonth ? formatCompetenceShort(pool.minimumBalanceMonth) : "—"}</td>
                    <td>{poolRisk.firstNegativeMonth ? formatCompetenceShort(poolRisk.firstNegativeMonth) : "Não fica"}</td>
                    <td>{compactCents(poolRisk.fundingGapCents)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </article>
        <aside className="director-module1__insight">
          <span className="director-eyebrow">Como ler</span>
          <h2>Sobra numa SPE não cobre a outra</h2>
          <p>{needing.length
            ? `${surplus.length ? `${joinNames(surplus)} não ${surplus.length === 1 ? "precisa" : "precisam"} de aporte no período. ` : ""}Para cobrir ${joinNames(needing)} com esse dinheiro é preciso uma operação entre as empresas, como mútuo, distribuição ou novo aporte.`
            : "Nenhum caixa precisa de aporte no período. Mesmo assim, a sobra de uma SPE só chega à outra por uma operação entre as empresas."}</p>
          <div className="director-module1__callout">O saldo projetado só aparece quando todos os caixas têm saldo inicial informado.</div>
        </aside>
      </section>
    </>}
    {data && freshness && <footer className="director-freshness">Fontes atualizadas em {formatFreshness(freshness)}.</footer>}
  </ModuleShell>;
};

export default Capital;
